import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

interface QueryProviderWithNavigationProps {
  children: React.ReactNode;
}

const QueryProviderWithNavigation: React.FC<QueryProviderWithNavigationProps> = ({ children }) => {
  const { isSignedIn } = useAuth();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
        retry: (failureCount, error) => {
          const errorMessage = (error as Error).message;
          if (errorMessage.includes("JWT expired") || errorMessage.includes("invalid JWT")) {
            return false; // Do not retry on JWT expiration
          }
          return failureCount < 3; // Retry up to 3 times for other errors
        },
        onError: (error) => {
          console.error("Global Query Error:", error);
          const errorMessage = (error as Error).message;

          if (isSignedIn && (errorMessage.includes("JWT expired") || errorMessage.includes("invalid JWT"))) {
            console.warn("JWT expired or invalid. Invalidating queries to force re-authentication.");
            queryClient.invalidateQueries(); // Invalidate all queries to force re-fetch
            // Clerk should handle re-authentication automatically.
            // If not, the SignedIn/SignedOut components in App.tsx will handle redirection.
          }
        },
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

export default QueryProviderWithNavigation;
